import { apiClient } from "./client";

export type EnergyType = "electricity" | "heat";
export type TariffType = "fixed" | "rdn_dynamic";
export type ExpectedDirections = "a_plus_only" | "a_minus_only" | "both";

export interface Client {
  id: string;
  tenant_id: string;
  name: string;
  code: string;
  tariff_type: TariffType;
  trader_fee_pct: number | string;
  is_active: boolean;
  created_at: string;
}

export interface ClientCreate {
  name: string;
  code: string;
  tariff_type: TariffType;
  trader_fee_pct?: number;
  is_active?: boolean;
}

export interface ClientUpdate {
  name?: string;
  tariff_type?: TariffType;
  trader_fee_pct?: number;
  is_active?: boolean;
}

export interface ClientMeter {
  id: string;
  client_id: string;
  source_id: string | null;
  name: string;
  energy_type: EnergyType;
  count_a_plus: boolean | null;
  count_a_minus: boolean | null;
  expected_directions: ExpectedDirections;
  created_at: string;
}

export interface ClientMeterCreate {
  name: string;
  energy_type: EnergyType;
  source_id?: string | null;
  count_a_plus?: boolean | null;
  count_a_minus?: boolean | null;
  expected_directions?: ExpectedDirections;
}

export interface ClientMeterUpdate {
  name?: string;
  source_id?: string | null;
  count_a_plus?: boolean | null;
  count_a_minus?: boolean | null;
  expected_directions?: ExpectedDirections;
}

export interface Tariff {
  id: string;
  client_id: string;
  energy_type: EnergyType;
  effective_from: string;
  fixed_price: number | string | null;
  created_by: string | null;
  created_at: string;
}

export interface TariffCreate {
  energy_type: EnergyType;
  effective_from: string;
  fixed_price?: number | null;
}

export const clientsApi = {
  list: (includeInactive = false) =>
    apiClient
      .get<Client[]>("/clients", { params: { include_inactive: includeInactive } })
      .then((r) => r.data),
  create: (body: ClientCreate) =>
    apiClient.post<Client>("/clients", body).then((r) => r.data),
  update: (id: string, body: ClientUpdate) =>
    apiClient.put<Client>(`/clients/${id}`, body).then((r) => r.data),
  deactivate: (id: string) =>
    apiClient.delete(`/clients/${id}`).then((r) => r.data),

  listMeters: (clientId: string, energyType?: EnergyType) =>
    apiClient
      .get<ClientMeter[]>(`/clients/${clientId}/meters`, {
        params: energyType ? { energy_type: energyType } : undefined,
      })
      .then((r) => r.data),
  createMeter: (clientId: string, body: ClientMeterCreate) =>
    apiClient
      .post<ClientMeter>(`/clients/${clientId}/meters`, body)
      .then((r) => r.data),
  updateMeter: (clientId: string, meterId: string, body: ClientMeterUpdate) =>
    apiClient
      .put<ClientMeter>(`/clients/${clientId}/meters/${meterId}`, body)
      .then((r) => r.data),
  deleteMeter: (clientId: string, meterId: string) =>
    apiClient.delete(`/clients/${clientId}/meters/${meterId}`).then((r) => r.data),

  listTariffs: (clientId: string, energyType?: EnergyType) =>
    apiClient
      .get<Tariff[]>(`/clients/${clientId}/tariffs`, {
        params: energyType ? { energy_type: energyType } : undefined,
      })
      .then((r) => r.data),
  createTariff: (clientId: string, body: TariffCreate) =>
    apiClient.post<Tariff>(`/clients/${clientId}/tariffs`, body).then((r) => r.data),
};
